import { useEffect, useState } from 'react';
import { apiClient } from '../../utils/apiClient';
import type { ModelConfigOption } from './comparisonTypes';

// Loads the active `role='investigation'` model configs for the replay picker (sub-plan 20).
// Reads the admin model-config list (GET /api/backend/ai-troubleshooting/model-configs, sub-plan 17 /
// REF-08 §3.2) directly via apiClient (self-contained; does not depend on dataProvider resource
// resolution) and filters to active investigation configs client-side. A `gate` or inactive config
// is never offered for replay. On error the hook surfaces `error` (no fabricated list).
export interface UseModelConfigsResult {
  configs: ModelConfigOption[];
  loading: boolean;
  error: string | null;
}

interface ModelConfigListResponse {
  data: ModelConfigOption[];
  total: number;
  limit: number;
  offset: number;
}

export const useModelConfigs = (): UseModelConfigsResult => {
  const [configs, setConfigs] = useState<ModelConfigOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    // Ask for a generous page so the small global model-config set is fully returned.
    apiClient
      .get<ModelConfigListResponse>(
        '/api/backend/ai-troubleshooting/model-configs?limit=200&offset=0',
      )
      .then((res) => {
        if (cancelled) return;
        const rows = Array.isArray(res?.data) ? res.data : [];
        // Only active investigation configs are valid replay targets (sub-plan 17-A).
        setConfigs(rows.filter((c) => c.isActive && c.role === 'investigation'));
      })
      .catch((err: { message?: string }) => {
        if (cancelled) return;
        setError(err?.message || 'Failed to load model configs');
        setConfigs([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return { configs, loading, error };
};

export default useModelConfigs;
